/**
 * Admin job details page
 */

import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useGetJobByIdQuery, useGetJobLogsQuery } from '@/services/api';

export default function AdminJobDetails() {
  const { id } = useParams<{ id: string }>();
  const jobId = parseInt(id || '0');

  const { data: job, isLoading: jobLoading } = useGetJobByIdQuery(jobId);
  const { data: logs } = useGetJobLogsQuery(jobId);

  if (jobLoading) {
    return <div className="skeleton h-96 w-full rounded-xl" />;
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-2">Job not found</h2>
        <Link to="/admin/jobs" className="link">
          ← Back to jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Link to="/admin/jobs" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
        <ArrowLeftIcon className="w-4 h-4" />
        Back to jobs
      </Link>

      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Job #{job.id}</h1>
            <p className="text-gray-400">{job.job_type}</p>
          </div>
          <span
            className={`badge ${
              job.status === 'completed'
                ? 'badge-info'
                : job.status === 'failed'
                ? 'badge-critical'
                : job.status === 'running'
                ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                : 'bg-gray-600/10 text-gray-400 border border-gray-600/20'
            }`}
          >
            {job.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <span className="text-gray-400">Scheduled:</span>{' '}
            <span className="text-white">{job.scheduled_at ? new Date(job.scheduled_at + 'Z').toLocaleString() : 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-400">Started:</span>{' '}
            <span className="text-white">{job.started_at ? new Date(job.started_at + 'Z').toLocaleString() : 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-400">Completed:</span>{' '}
            <span className="text-white">{job.completed_at ? new Date(job.completed_at + 'Z').toLocaleString() : 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-400">Duration:</span>{' '}
            <span className="text-white">
              {job.duration_ms
                ? job.duration_ms < 1000 ? `${job.duration_ms}ms`
                  : job.duration_ms < 60000 ? `${(job.duration_ms / 1000).toFixed(1)}s`
                  : `${(job.duration_ms / 60000).toFixed(1)}m`
                : 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-gray-400">Triggered by:</span>{' '}
            <span className="text-white capitalize">{job.triggered_by || 'N/A'}</span>
          </div>
          {job.software_name && (
            <div>
              <span className="text-gray-400">Software:</span>{' '}
              <span className="text-white">{job.software_name}</span>
            </div>
          )}
        </div>

        {/* Result summary */}
        {job.result && (() => {
          try {
            const r = JSON.parse(job.result);
            return (
              <div className="border-t border-gray-700 pt-4 mt-2">
                <div className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">Result</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(r).map(([k, v]) => v != null && (
                    <div key={k} className="bg-gray-800/50 rounded p-2">
                      <div className="text-xs text-gray-400">{k.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-white font-medium mt-0.5">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          } catch {
            return (
              <div className="border-t border-gray-700 pt-4 mt-2">
                <div className="text-xs text-gray-400 mb-1 font-semibold uppercase tracking-wide">Result</div>
                <div className="text-sm text-gray-300 font-mono">{job.result}</div>
              </div>
            );
          }
        })()}

        {/* Error */}
        {job.error_message && (
          <div className="border-t border-gray-700 pt-4 mt-2">
            <div className="text-xs text-red-400 mb-1 font-semibold uppercase tracking-wide">Error</div>
            <div className="text-sm text-red-300 font-mono bg-red-900/10 rounded p-3">{job.error_message}</div>
          </div>
        )}
      </div>

      {/* Logs */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Execution Logs</h2>
        {!logs || logs.length === 0 ? (
          <div className="text-gray-400 py-8 text-center">No logs available</div>
        ) : (
          <div className="bg-black rounded-lg p-4 font-mono text-sm space-y-1 max-h-96 overflow-auto scrollbar-thin">
            {logs.map((log) => (
              <div key={log.id} className={`${
                log.log_level === 'error' ? 'text-severity-critical' :
                log.log_level === 'warn' ? 'text-severity-medium' :
                log.log_level === 'info' ? 'text-cyber-blue-400' :
                'text-gray-500'
              }`}>
                <span className="text-gray-600">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
                <span className="text-gray-500">{log.log_level.toUpperCase()}</span>{' '}
                {log.message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
